import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

function getHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('adminToken')}` };
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function daysBetween(a, b) {
  if (!a || !b) return null;
  return Math.floor((new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24));
}

const SEV_COLOR = { critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#22c55e' };
const SEV_BG    = { critical: '#450a0a', high: '#431407', medium: '#422006', low: '#14532d' };
const STATUS_COLOR = { success: '#22c55e', failed: '#ef4444', pending: '#f59e0b', in_progress: '#60a5fa' };
const STATUS_BG    = { success: '#14532d', failed: '#450a0a', pending: '#422006', in_progress: '#1e3a5f' };
const STATUS_ICON  = { success: '✔', failed: '✘', pending: '⏳', in_progress: '⟳' };

export default function PatchDeployment({ user }) {
  const [patches,   setPatches]   = useState([]);
  const [statuses,  setStatuses]  = useState([]);
  const [devices,   setDevices]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [view,      setView]      = useState('by-patch');   // 'by-patch' | 'by-device' | 'timeline'
  const [search,    setSearch]    = useState('');
  const [filterSev, setFilterSev] = useState('all');
  const [filterSts, setFilterSts] = useState('all');
  const [expanded,  setExpanded]  = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, d] = await Promise.all([
        axios.get('/api/patches/all',        { headers: getHeaders() }),
        axios.get('/api/patches/compliance', { headers: getHeaders() }),
        axios.get('/api/agents',             { headers: getHeaders() })
      ]);
      setPatches(p.data);
      setStatuses(s.data.details || []);
      setDevices(d.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Build patch → deployments map ─────────────────────────────────────────
  const patchMap = {};
  patches.forEach(p => { patchMap[p.id] = p; });

  const deviceMap = {};
  devices.forEach(d => { deviceMap[d.id] = d; });

  // Group statuses by patch
  const byPatch = {};
  statuses.forEach(s => {
    const pid = s.patch?.id || s.patchId;
    if (!pid) return;
    if (!byPatch[pid]) byPatch[pid] = { patch: s.patch, deployments: [] };
    byPatch[pid].deployments.push(s);
  });

  // Group statuses by device
  const byDevice = {};
  statuses.forEach(s => {
    const did = s.device?.id || s.deviceId;
    if (!did) return;
    if (!byDevice[did]) byDevice[did] = { device: s.device, deployments: [] };
    byDevice[did].deployments.push(s);
  });

  // Summary stats
  const totalDeployments = statuses.length;
  const successCount     = statuses.filter(s => s.status === 'success').length;
  const failedCount      = statuses.filter(s => s.status === 'failed').length;
  const pendingCount     = statuses.filter(s => s.status === 'pending').length;
  const inProgressCount  = statuses.filter(s => s.status === 'in_progress').length;

  // Filter patches
  const filteredPatches = patches.filter(p => {
    const matchSearch = !search || [p.name, p.vendor, p.product, p.version].join(' ').toLowerCase().includes(search.toLowerCase());
    const matchSev    = filterSev === 'all' || p.severity === filterSev;
    const hasStatus   = byPatch[p.id];
    const matchSts    = filterSts === 'all' || (hasStatus && byPatch[p.id].deployments.some(d => d.status === filterSts));
    return matchSearch && matchSev && (filterSts === 'all' ? true : matchSts);
  });

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">Patch Release & Deployment</div>
          <div className="page-subtitle">Release dates, deployment status and timeline per device</div>
        </div>
        <div className="flex-gap">
          <button className="refresh-btn" onClick={load}>↻ Refresh</button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="cards-row">
        <div className="card blue" style={{ cursor:'pointer' }} onClick={() => setFilterSts('all')}>
          <div className="card-label">Total Patches</div>
          <div className="card-value">{patches.length}</div>
          <div className="card-sub">{filterSts==='all' ? '▼ showing all' : 'in catalog'}</div>
        </div>
        <div className="card blue" style={{ cursor:'pointer' }} onClick={() => setFilterSts('all')}>
          <div className="card-label">Deployments</div>
          <div className="card-value">{totalDeployments}</div>
          <div className="card-sub">total tasks</div>
        </div>
        <div className="card green" style={{ cursor:'pointer', outline: filterSts==='success'?'2px solid #16a34a':undefined }}
          onClick={() => setFilterSts(filterSts==='success'?'all':'success')}>
          <div className="card-label">Successful</div>
          <div className="card-value">{successCount}</div>
          <div className="card-sub">{filterSts==='success' ? '▼ filtered' : `${totalDeployments > 0 ? Math.round(successCount/totalDeployments*100) : 0}% success rate`}</div>
        </div>
        <div className="card red" style={{ cursor:'pointer', outline: filterSts==='failed'?'2px solid #dc2626':undefined }}
          onClick={() => setFilterSts(filterSts==='failed'?'all':'failed')}>
          <div className="card-label">Failed</div>
          <div className="card-value">{failedCount}</div>
          <div className="card-sub">{filterSts==='failed' ? '▼ filtered' : 'click to filter'}</div>
        </div>
        <div className="card yellow" style={{ cursor:'pointer', outline: filterSts==='pending'?'2px solid #d97706':undefined }}
          onClick={() => setFilterSts(filterSts==='pending'?'all':'pending')}>
          <div className="card-label">Pending</div>
          <div className="card-value">{pendingCount}</div>
          <div className="card-sub">{filterSts==='pending' ? '▼ filtered' : 'click to filter'}</div>
        </div>
        <div className="card cyan" style={{ cursor:'pointer', outline: filterSts==='in_progress'?'2px solid #1a56db':undefined }}
          onClick={() => setFilterSts(filterSts==='in_progress'?'all':'in_progress')}>
          <div className="card-label">In Progress</div>
          <div className="card-value">{inProgressCount}</div>
          <div className="card-sub">{filterSts==='in_progress' ? '▼ filtered' : 'click to filter'}</div>
        </div>
      </div>

      {/* View tabs + filters */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div className="flex-gap">
          {[['by-patch','📦 By Patch'],['by-device','🖥️ By Device'],['timeline','📅 Timeline']].map(([id, label]) => (
            <button key={id} className={`btn ${view === id ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView(id)}>{label}</button>
          ))}
        </div>
        <div className="flex-gap">
          <select style={{ background:'#0f1117', border:'1px solid #2d3148', color:'#e2e8f0', borderRadius:6, padding:'5px 10px', fontSize:12 }}
            value={filterSev} onChange={e => setFilterSev(e.target.value)}>
            <option value="all">All Severity</option>
            {['critical','high','medium','low'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select style={{ background:'#0f1117', border:'1px solid #2d3148', color:'#e2e8f0', borderRadius:6, padding:'5px 10px', fontSize:12 }}
            value={filterSts} onChange={e => setFilterSts(e.target.value)}>
            <option value="all">All Status</option>
            {['success','failed','pending','in_progress'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <input className="form-input" style={{ width:200, padding:'5px 10px', fontSize:12 }}
            placeholder="Search patch / vendor..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {loading && <div className="loading">Loading deployment data...</div>}

      {/* ── BY PATCH VIEW ── */}
      {!loading && view === 'by-patch' && (
        <div>
          {filteredPatches.length === 0 && <div className="table-wrap"><div className="empty">No patches found.</div></div>}
          {filteredPatches.map(patch => {
            const info = byPatch[patch.id];
            const deps = info?.deployments || [];
            const successDeps = deps.filter(d => d.status === 'success');
            const isOpen = expanded[patch.id];

            return (
              <div key={patch.id} className="table-wrap section-gap" style={{ marginBottom: 12 }}>
                {/* Patch header row */}
                <div
                  style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: isOpen ? '1px solid #2d3148' : 'none' }}
                  onClick={() => toggle(patch.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <span style={{ fontSize: 16 }}>{isOpen ? '▼' : '▶'}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{patch.name}</div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                        {patch.vendor} · {patch.product} · v{patch.version} · {patch.osType} · {patch.category}
                      </div>
                    </div>
                  </div>
                  <div className="flex-gap">
                    <span className="badge" style={{ background: SEV_BG[patch.severity], color: SEV_COLOR[patch.severity] }}>{patch.severity}</span>
                    <div style={{ textAlign: 'right', fontSize: 12 }}>
                      <div style={{ color: '#64748b' }}>Released</div>
                      <div style={{ color: '#e2e8f0' }}>{fmtDate(patch.releaseDate || patch.createdAt)}</div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 12 }}>
                      <div style={{ color: '#64748b' }}>Deployed to</div>
                      <div style={{ color: successDeps.length > 0 ? '#22c55e' : '#64748b' }}>
                        {successDeps.length} / {deps.length} devices
                      </div>
                    </div>
                    {deps.length > 0 && (
                      <div style={{ width: 80 }}>
                        <div className="progress-bar-wrap">
                          <div className={`progress-bar ${deps.length > 0 && successDeps.length === deps.length ? 'green' : ''}`}
                            style={{ width: `${deps.length > 0 ? Math.round(successDeps.length/deps.length*100) : 0}%` }} />
                        </div>
                        <div style={{ fontSize: 10, color: '#64748b', textAlign: 'center', marginTop: 2 }}>
                          {deps.length > 0 ? Math.round(successDeps.length/deps.length*100) : 0}%
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Expanded device list */}
                {isOpen && (
                  <div>
                    {deps.length === 0 ? (
                      <div className="empty" style={{ padding: 20 }}>Not deployed to any device yet.</div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ padding:'8px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Device</th>
                            <th style={{ padding:'8px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>OS</th>
                            <th style={{ padding:'8px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Status</th>
                            <th style={{ padding:'8px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Patch Released</th>
                            <th style={{ padding:'8px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Deployed On</th>
                            <th style={{ padding:'8px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Days to Deploy</th>
                            <th style={{ padding:'8px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deps.map((dep, i) => {
                            const releaseDate  = patch.releaseDate || patch.createdAt;
                            const deployedDate = dep.completedAt;
                            const days = daysBetween(releaseDate, deployedDate);
                            return (
                              <tr key={i} style={{ borderBottom: '1px solid #1e2235' }}>
                                <td style={{ padding:'10px 16px', fontSize:13 }}><strong>{dep.device?.hostname || '—'}</strong></td>
                                <td style={{ padding:'10px 16px', fontSize:12, color:'#94a3b8' }}>{dep.device?.os || '—'}</td>
                                <td style={{ padding:'10px 16px' }}>
                                  <span className="badge" style={{ background: STATUS_BG[dep.status], color: STATUS_COLOR[dep.status] }}>
                                    {STATUS_ICON[dep.status]} {dep.status}
                                  </span>
                                </td>
                                <td style={{ padding:'10px 16px', fontSize:12, color:'#94a3b8' }}>{fmtDate(releaseDate)}</td>
                                <td style={{ padding:'10px 16px', fontSize:12, color: deployedDate ? '#e2e8f0' : '#64748b' }}>{fmtDateTime(deployedDate)}</td>
                                <td style={{ padding:'10px 16px', fontSize:12 }}>
                                  {days !== null ? (
                                    <span style={{ color: days <= 14 ? '#22c55e' : days <= 30 ? '#f59e0b' : '#ef4444', fontWeight: 600 }}>
                                      {days}d {days <= 14 ? '✔' : '⚠'}
                                    </span>
                                  ) : '—'}
                                </td>
                                <td style={{ padding:'10px 16px', fontSize:11, color:'#ef4444', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                  {dep.error ? dep.error.substring(0, 80) : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── BY DEVICE VIEW ── */}
      {!loading && view === 'by-device' && (
        <div>
          {Object.entries(byDevice).length === 0 && <div className="table-wrap"><div className="empty">No deployment data.</div></div>}
          {Object.entries(byDevice).map(([deviceId, info]) => {
            const deps    = info.deployments;
            const success = deps.filter(d => d.status === 'success').length;
            const isOpen  = expanded[deviceId];

            return (
              <div key={deviceId} className="table-wrap section-gap" style={{ marginBottom: 12 }}>
                <div
                  style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: isOpen ? '1px solid #2d3148' : 'none' }}
                  onClick={() => toggle(deviceId)}
                >
                  <div className="flex-gap">
                    <span style={{ fontSize: 16 }}>{isOpen ? '▼' : '▶'}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>🖥️ {info.device?.hostname || deviceId}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{info.device?.os || 'unknown OS'}</div>
                    </div>
                  </div>
                  <div className="flex-gap">
                    <div style={{ textAlign: 'right', fontSize: 12 }}>
                      <div style={{ color: '#64748b' }}>Patches</div>
                      <div style={{ color: '#e2e8f0' }}>{deps.length} total</div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 12 }}>
                      <div style={{ color: '#64748b' }}>Deployed</div>
                      <div style={{ color: success > 0 ? '#22c55e' : '#64748b' }}>{success} success</div>
                    </div>
                    <div style={{ width: 80 }}>
                      <div className="progress-bar-wrap">
                        <div className={`progress-bar ${success === deps.length ? 'green' : ''}`}
                          style={{ width: `${deps.length > 0 ? Math.round(success/deps.length*100) : 0}%` }} />
                      </div>
                      <div style={{ fontSize: 10, color: '#64748b', textAlign: 'center', marginTop: 2 }}>
                        {deps.length > 0 ? Math.round(success/deps.length*100) : 0}%
                      </div>
                    </div>
                  </div>
                </div>

                {isOpen && (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ padding:'8px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Patch</th>
                        <th style={{ padding:'8px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Vendor</th>
                        <th style={{ padding:'8px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Version</th>
                        <th style={{ padding:'8px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Severity</th>
                        <th style={{ padding:'8px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Status</th>
                        <th style={{ padding:'8px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Released</th>
                        <th style={{ padding:'8px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Deployed</th>
                        <th style={{ padding:'8px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deps.map((dep, i) => {
                        const patch = patchMap[dep.patch?.id] || dep.patch || {};
                        const releaseDate  = patch.releaseDate || patch.createdAt;
                        const deployedDate = dep.completedAt;
                        const days = daysBetween(releaseDate, deployedDate);
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid #1e2235' }}>
                            <td style={{ padding:'10px 16px', fontSize:13 }}>{dep.patch?.name || '—'}</td>
                            <td style={{ padding:'10px 16px', fontSize:12, color:'#94a3b8' }}>{dep.patch?.vendor || '—'}</td>
                            <td style={{ padding:'10px 16px', fontSize:12, color:'#94a3b8' }}>{dep.patch?.version || '—'}</td>
                            <td style={{ padding:'10px 16px' }}>
                              {patch.severity && <span className="badge" style={{ background: SEV_BG[patch.severity], color: SEV_COLOR[patch.severity] }}>{patch.severity}</span>}
                            </td>
                            <td style={{ padding:'10px 16px' }}>
                              <span className="badge" style={{ background: STATUS_BG[dep.status], color: STATUS_COLOR[dep.status] }}>
                                {STATUS_ICON[dep.status]} {dep.status}
                              </span>
                            </td>
                            <td style={{ padding:'10px 16px', fontSize:12, color:'#94a3b8' }}>{fmtDate(releaseDate)}</td>
                            <td style={{ padding:'10px 16px', fontSize:12 }}>{fmtDateTime(deployedDate)}</td>
                            <td style={{ padding:'10px 16px', fontSize:12 }}>
                              {days !== null ? (
                                <span style={{ color: days <= 14 ? '#22c55e' : '#f59e0b', fontWeight: 600 }}>{days}d</span>
                              ) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── TIMELINE VIEW ── */}
      {!loading && view === 'timeline' && (
        <div className="table-wrap">
          <div className="table-header">
            <div className="table-title">📅 Deployment Timeline — All Patches × All Devices</div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding:'10px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Patch Name</th>
                <th style={{ padding:'10px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Vendor</th>
                <th style={{ padding:'10px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Version</th>
                <th style={{ padding:'10px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Severity</th>
                <th style={{ padding:'10px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Category</th>
                <th style={{ padding:'10px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Device</th>
                <th style={{ padding:'10px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Status</th>
                <th style={{ padding:'10px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Patch Released</th>
                <th style={{ padding:'10px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Deployed On</th>
                <th style={{ padding:'10px 16px', fontSize:11, color:'#64748b', textTransform:'uppercase', background:'#151720', textAlign:'left' }}>Days to Deploy</th>
              </tr>
            </thead>
            <tbody>
              {statuses.length === 0 && (
                <tr><td colSpan={10} className="empty">No deployment records.</td></tr>
              )}
              {[...statuses]
                .filter(s => {
                  const matchSearch = !search || [s.patch?.name, s.patch?.vendor, s.device?.hostname].join(' ').toLowerCase().includes(search.toLowerCase());
                  const matchSev    = filterSev === 'all' || (patchMap[s.patch?.id]?.severity === filterSev);
                  const matchSts    = filterSts === 'all' || s.status === filterSts;
                  return matchSearch && matchSev && matchSts;
                })
                .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
                .map((s, i) => {
                  const patch = patchMap[s.patch?.id] || s.patch || {};
                  const releaseDate  = patch.releaseDate || patch.createdAt;
                  const deployedDate = s.completedAt;
                  const days = daysBetween(releaseDate, deployedDate);
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #1e2235' }}>
                      <td style={{ padding:'10px 16px', fontSize:13, fontWeight:500 }}>{s.patch?.name || '—'}</td>
                      <td style={{ padding:'10px 16px', fontSize:12, color:'#94a3b8' }}>{s.patch?.vendor || '—'}</td>
                      <td style={{ padding:'10px 16px', fontSize:12, color:'#94a3b8' }}>{s.patch?.version || '—'}</td>
                      <td style={{ padding:'10px 16px' }}>
                        {patch.severity && <span className="badge" style={{ background: SEV_BG[patch.severity], color: SEV_COLOR[patch.severity] }}>{patch.severity}</span>}
                      </td>
                      <td style={{ padding:'10px 16px', fontSize:12, color:'#94a3b8' }}>{patch.category || '—'}</td>
                      <td style={{ padding:'10px 16px', fontSize:13 }}><strong>{s.device?.hostname || '—'}</strong></td>
                      <td style={{ padding:'10px 16px' }}>
                        <span className="badge" style={{ background: STATUS_BG[s.status], color: STATUS_COLOR[s.status] }}>
                          {STATUS_ICON[s.status]} {s.status}
                        </span>
                      </td>
                      <td style={{ padding:'10px 16px', fontSize:12, color:'#94a3b8' }}>{fmtDate(releaseDate)}</td>
                      <td style={{ padding:'10px 16px', fontSize:12, color: deployedDate ? '#e2e8f0' : '#64748b' }}>{fmtDateTime(deployedDate)}</td>
                      <td style={{ padding:'10px 16px', fontSize:12 }}>
                        {days !== null ? (
                          <span style={{ color: days <= 14 ? '#22c55e' : days <= 30 ? '#f59e0b' : '#ef4444', fontWeight: 600 }}>
                            {days}d {days <= 14 ? '✔' : '⚠'}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
